/**
 * Client-side access-code session (Phase 3d).
 *
 * After a successful Stripe purchase, verify-session returns an access code +
 * balance, which we keep in localStorage so returning buyers stay unlocked on
 * this browser. The balance shown here is best-effort UI only — the server's
 * credit store is authoritative and the /api/interpret response decrements it.
 */

const SESSION_KEY = "ga_access_session_v1";

/**
 * @typedef {{ email: string, code: string, balance: number } | null} AccessStore
 */

/** @returns {AccessStore} */
function readStore() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.email && parsed.code) return parsed;
    return null;
  } catch {
    return null;
  }
}

/** @param {AccessStore} store */
function writeStore(store) {
  try {
    if (!store) localStorage.removeItem(SESSION_KEY);
    else localStorage.setItem(SESSION_KEY, JSON.stringify(store));
  } catch {
    /* storage unavailable — session just won't persist */
  }
}

/** True when a valid access session (email + code) is stored. */
export function hasAccessSession() {
  const s = readStore();
  return Boolean(s && s.email && s.code);
}

export function getAccessEmail() {
  return readStore()?.email || null;
}

export function getAccessCode() {
  return readStore()?.code || null;
}

export function getAccessBalance() {
  const s = readStore();
  return Number.isFinite(s?.balance) ? s.balance : null;
}

/**
 * Save (or update) the stored access session.
 * @param {{ email: string, code: string, balance?: number }} s
 */
export function setAccessSession({ email, code, balance }) {
  if (!email || !code) return;
  writeStore({ email, code, balance: Number.isFinite(balance) ? balance : getAccessBalance() });
}

/** Remove any saved access session. */
export function clearAccessSession() {
  writeStore(null);
}

/**
 * Auth payload to send to /api/interpret when an access session is active.
 * Returns null when the user has no stored access code.
 */
export function buildAccessAuth() {
  if (!hasAccessSession()) return null;
  return {
    type: "access",
    email: getAccessEmail(),
    code: getAccessCode(),
  };
}

/**
 * Optimistically reduce the stored balance by one after a successful server
 * generation (authoritative value lives server-side; this is just UI feedback).
 */
export function decrementAccessBalance() {
  const s = readStore();
  if (!s) return null;
  const b = Number.isFinite(s.balance) ? Math.max(0, s.balance - 1) : null;
  writeStore({ ...s, balance: b });
  return b;
}

/** Refresh the stored balance with a fresh value from the server. */
export function refreshAccessBalance(balance) {
  const s = readStore();
  if (!s) return;
  const b = Number.isFinite(balance) ? Math.max(0, balance) : s.balance;
  writeStore({ ...s, balance: b });
}